import { useEffect, useMemo, useState, type MouseEvent } from 'react';
import { Box, Button, ListItemButton, Popover, Stack, Typography } from '@mui/material';
import { ArrowDropDown, Check } from '@mui/icons-material';
import dayjs from 'dayjs';
import { format, startOfMonth } from 'date-fns';
import { CustomDateCalendar } from '../../../../components/common/CustomDateCalendar';
import { CustomDatePicker, DATE_PICKER_MODE } from '../../../../components/common/CustomDatePicker';
import {
  calculateDateRange,
  formatDateTimeForInput,
  getGridDateTimeMode,
  toGridFilterEndOfDay,
  toGridFilterStartOfDay,
} from '../../utils';
import type { DateFilterRendererProps } from './DateRangeFilter';
import {
  ADVANCED_DATE_RANGE_PRESETS,
  getMatchingAdvancedPreset,
  orderDateRange,
  type DateRangeValue,
} from './advancedDateRange.utils';

const formatDate = (date: Date) => format(date, 'MMM d, yyyy');

const resolveInitialRange = ({
  command,
  initialDateRange,
}: Pick<DateFilterRendererProps, 'command' | 'initialDateRange'>): DateRangeValue => {
  if (initialDateRange?.startDate && initialDateRange.endDate) {
    const dateTimeMode = getGridDateTimeMode(command.settings);
    const startDate = formatDateTimeForInput(initialDateRange.startDate, 'datetime', dateTimeMode);
    const endDate = formatDateTimeForInput(initialDateRange.endDate, 'datetime', dateTimeMode);
    if (startDate instanceof Date && endDate instanceof Date) {
      return {
        startDate,
        endDate,
      };
    }
  }

  return calculateDateRange(command.defaultRange ?? 'Current month');
};

export const AdvancedDateRangeFilterRenderer = ({
  command,
  onDateRangeChange,
  initialDateRange,
}: DateFilterRendererProps) => {
  const initialRange = useMemo(
    () => resolveInitialRange({ command, initialDateRange }),
    [command, initialDateRange]
  );
  const [range, setRange] = useState(initialRange);
  const [draftStart, setDraftStart] = useState<Date>(initialRange.startDate);
  const [draftEnd, setDraftEnd] = useState<Date | null>(initialRange.endDate);
  const [viewMonth, setViewMonth] = useState(startOfMonth(initialRange.endDate));
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const dateTimeMode = getGridDateTimeMode(command.settings);

  useEffect(() => {
    setRange(initialRange);
    setDraftStart(initialRange.startDate);
    setDraftEnd(initialRange.endDate);
    setViewMonth(startOfMonth(initialRange.endDate));
  }, [initialRange]);

  const commitRange = (nextRange: DateRangeValue, close = true) => {
    setRange(nextRange);
    setDraftStart(nextRange.startDate);
    setDraftEnd(nextRange.endDate);
    onDateRangeChange(
      toGridFilterStartOfDay(dayjs(nextRange.startDate), dateTimeMode),
      toGridFilterEndOfDay(dayjs(nextRange.endDate), dateTimeMode)
    );
    if (close) setAnchorElement(null);
  };

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    setDraftStart(range.startDate);
    setDraftEnd(range.endDate);
    setViewMonth(startOfMonth(range.endDate));
    setAnchorElement(event.currentTarget);
  };

  const handleCalendarDay = (day: Date) => {
    if (draftEnd) {
      setDraftStart(day);
      setDraftEnd(null);
      return;
    }

    commitRange(orderDateRange(draftStart, day));
  };

  const selectedPreset = draftEnd ? getMatchingAdvancedPreset(draftStart, draftEnd) : null;

  return (
    <>
      <Button
        variant="outlined"
        endIcon={<ArrowDropDown />}
        onClick={handleOpen}
        aria-label={command.caption || 'Select date range'}
        aria-expanded={anchorElement != null}
        sx={{
          width: 320,
          maxWidth: '100%',
          height: 40,
          justifyContent: 'space-between',
          color: 'text.primary',
          borderColor: 'divider',
          textTransform: 'none',
          fontWeight: 400,
        }}
      >
        {formatDate(range.startDate)} - {formatDate(range.endDate)}
      </Button>

      <Popover
        open={anchorElement != null}
        anchorEl={anchorElement}
        onClose={() => setAnchorElement(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { width: 320, maxWidth: 'calc(100vw - 16px)', p: 1 } } }}
      >
        <Stack sx={{ maxHeight: 'min(680px, calc(100vh - 100px))', overflowY: 'auto' }}>
          {ADVANCED_DATE_RANGE_PRESETS.map((preset) => {
            const presetRange = calculateDateRange(preset);
            return (
              <ListItemButton
                key={preset}
                selected={selectedPreset === preset}
                onClick={() => commitRange(presetRange)}
                sx={{ gap: 1, py: 0.5 }}
              >
                <Box sx={{ width: 20, color: 'primary.main', flexShrink: 0 }}>
                  {selectedPreset === preset && <Check fontSize="small" />}
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {preset}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(presetRange.startDate)} - {formatDate(presetRange.endDate)}
                  </Typography>
                </Box>
              </ListItemButton>
            );
          })}

          <ListItemButton selected={selectedPreset == null} sx={{ gap: 1, py: 0.5 }}>
            <Box sx={{ width: 20, color: 'primary.main', flexShrink: 0 }}>
              {selectedPreset == null && <Check fontSize="small" />}
            </Box>
            <Box>
              <Typography variant="body2" fontWeight={600}>
                Custom date range
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatDate(draftStart)} - {formatDate(draftEnd ?? draftStart)}
              </Typography>
            </Box>
          </ListItemButton>

          <CustomDateCalendar
            viewMonth={viewMonth}
            selectedDate={draftEnd ?? draftStart}
            rangeStartDate={draftStart}
            rangeEndDate={draftEnd}
            mode={DATE_PICKER_MODE.DATE}
            onViewMonthChange={setViewMonth}
            onSelectDay={handleCalendarDay}
            onSelectMonth={setViewMonth}
            onTimeChange={() => undefined}
          />

          <Stack direction="row" spacing={1} sx={{ px: 0.5, pb: 0.5 }}>
            <CustomDatePicker
              compact
              label="Start Date"
              value={draftStart}
              mode={DATE_PICKER_MODE.DATE}
              onChange={(value) => {
                if (!value) return;
                const nextRange = orderDateRange(value, draftEnd ?? value);
                commitRange(nextRange, false);
              }}
            />
            <CustomDatePicker
              compact
              label="End Date"
              value={draftEnd}
              mode={DATE_PICKER_MODE.DATE}
              onChange={(value) => {
                if (!value) return;
                commitRange(orderDateRange(draftStart, value), false);
              }}
            />
          </Stack>
        </Stack>
      </Popover>
    </>
  );
};
